import React, { Component } from 'react';
import { FieldWrapper } from './FieldWrapper';
import { parseRenderOptions } from './utils';
import classNames from 'classnames';

export class DynamicCheckboxField extends Component {
  constructor(props, context) {
    super(props, context);
    // Store indexes of selected options as the state so we can differentiate a
    // typed value from a checked value
    this.state = {
      indexes: this.props.value
        .map((value, index) =>
          props.options.find(o => o.get('value') === value) ? index : null,
        )
        .filter(index => index !== null),
    };
  }

  // Handle toggle of predefined options
  onToggle = value => event => {
    // If unchecked, find index of value to remove
    const indexToRemove =
      !event.target.checked &&
      this.props.value.findIndex(
        (v, i) => v === value && this.state.indexes.includes(i),
      );

    if (typeof indexToRemove === 'number' && indexToRemove > -1) {
      // Remove the index from state when unchecking a predefined option and
      // decrement all successive state indexes
      this.setState(state => ({
        indexes: state.indexes
          .filter(index => index !== indexToRemove)
          .map(i => (i > indexToRemove ? i - 1 : i)),
      }));

      this.props.onChange(this.props.value.delete(indexToRemove));
    } else {
      // Add the index to state when checking a predefined option
      this.setState(state => ({
        indexes: state.indexes.push(this.props.value.size),
      }));

      this.props.onChange(this.props.value.push(value));
    }
  };

  // Handle edit of custom options
  onEdit = index => event => {
    this.props.onChange(
      event.target.value
        ? this.props.value.set(index, event.target.value)
        : this.props.value.delete(index),
    );
  };

  // Handle add of custom options
  onAdd = event => {
    this.props.onChange(this.props.value.push(event.target.value));
  };

  // Handle remove of custom options
  onRemove = index => () => {
    // When removing a custom option, decrement all indexes in state that came
    // after this option wsince those indexes will change
    this.setState(state => ({
      indexes: state.indexes.map(i => (i > index ? i - 1 : i)),
    }));

    this.props.onChange(this.props.value.delete(index));
  };

  // When rendering the inputs we append an empty string to the list of values,
  // this is helpful because then the "new" input is in the keyed collection so
  // when text is entered there we get a smooth addition of another new input.
  render() {
    const renderOptions = parseRenderOptions(
      this.props,
      { isFloating: false },
      { wrapperClassName: 'form-group--check' },
    );

    return (
      <FieldWrapper {...this.props} renderOptions={renderOptions} fieldset>
        {this.props.options.map(
          (option, i) =>
            option && (
              <div
                className={classNames('form-check', {
                  'form-check-sm': renderOptions.size === 'sm',
                })}
                key={`options-${i}`}
              >
                <input
                  type="checkbox"
                  id={`options-${i}`}
                  className="form-check-input"
                  onChange={this.onToggle(option.get('value'))}
                  checked={
                    !!this.props.value.find(
                      (v, i) =>
                        v === option.get('value') &&
                        this.state.indexes.includes(i),
                    )
                  }
                  disabled={!this.props.enabled}
                  value={option.get('value')}
                />
                <label htmlFor={`options-${i}`}>{option.get('value')}</label>
              </div>
            ),
        )}
        {this.props.value
          .concat(this.props.enabled ? [''] : [])
          .map((value, i) => {
            const matchesOption =
              !!this.props.options.find(o => o.get('value') === value) &&
              this.state.indexes.includes(i);
            // Show custom value if it doesn't match an option or if there are
            // multiple same values and this isn't the first one
            return !matchesOption ? (
              <div
                className={classNames('form-check', {
                  'form-check-sm': renderOptions.size === 'sm',
                })}
                key={`value-${i}`}
              >
                {value && (
                  <input
                    type="checkbox"
                    className="form-check-input"
                    onChange={this.onRemove(i)}
                    checked
                    disabled={!this.props.enabled}
                  />
                )}
                <input
                  type="text"
                  className={classNames('form-control', {
                    'form-control-sm': renderOptions.size === 'sm',
                  })}
                  disabled={!this.props.enabled}
                  onBlur={this.props.onBlur}
                  onChange={value ? this.onEdit(i) : this.onAdd}
                  onFocus={this.props.onFocus}
                  placeholder={this.props.placeholder}
                  value={value}
                />
              </div>
            ) : null;
          })}
      </FieldWrapper>
    );
  }
}
